/**
 * Backtest Results Display
 * Display backtest results with metrics and charts
 */

import { Card, CardHeader, CardTitle, CardContent } from '../trading/card'
import { MetricCard } from '../trading/metric-card'
import { TradingLineChart, TradingBarChart } from '../trading/chart'
import { Badge, StatusBadge } from '../trading/badge'
import { ProgressBar } from '../trading/progress-bar'
import { TrendingUp, TrendingDown, DollarSign, BarChart3, Activity, Target } from 'lucide-react'
import { formatNumber, formatPercent } from '@/lib/utils'

interface BacktestResultsProps {
  results: any
  configuration: any
}

export function BacktestResults({ results, configuration }: BacktestResultsProps) {
  if (!results) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No results available</p>
        </CardContent>
      </Card>
    )
  }

  const metrics = [
    {
      title: 'Total Return',
      value: formatPercent(results.totalReturnPercent),
      change: results.totalReturn,
      changeType: 'absolute',
      icon: <DollarSign className="h-6 w-6" />,
      trend: results.totalReturn >= 0 ? 'up' : 'down',
    },
    {
      title: 'Win Rate',
      value: formatPercent(results.winRate),
      icon: <Target className="h-6 w-6" />,
      trend: results.winRate >= 50 ? 'up' : 'down',
    },
    {
      title: 'Profit Factor',
      value: results.profitFactor.toFixed(2),
      icon: <Activity className="h-6 w-6" />,
      trend: results.profitFactor >= 2 ? 'up' : results.profitFactor >= 1 ? 'neutral' : 'down',
    },
    {
      title: 'Sharpe Ratio',
      value: results.sharpeRatio.toFixed(2),
      icon: <TrendingUp className="h-6 w-6" />,
      trend: results.sharpeRatio >= 1 ? 'up' : 'neutral',
    },
    {
      title: 'Max Drawdown',
      value: formatPercent(results.maxDrawdown),
      icon: <TrendingDown className="h-6 w-6" />,
      trend: 'down',
      variant: 'danger' as const,
    },
    {
      title: 'Total Trades',
      value: results.totalTrades.toString(),
      icon: <BarChart3 className="h-6 w-6" />,
    },
  ]

  // Prepare equity curve data
  const equityCurveData = results.equityCurve?.map((point: any) => ({
    date: new Date(point.date).toLocaleDateString(),
    value: point.value,
    return: ((point.value - configuration.initialCapital) / configuration.initialCapital) * 100,
  })) || []

  // Prepare trade distribution data
  const winningTrades = results.trades?.filter((t: any) => t.pnl && t.pnl > 0).length || 0
  const losingTrades = results.trades?.filter((t: any) => t.pnl && t.pnl < 0).length || 0
  const tradeDistribution = [
    { name: 'Winning', value: winningTrades, color: '#059669' },
    { name: 'Losing', value: losingTrades, color: '#dc2626' },
  ]

  // Calculate monthly returns
  const monthlyReturns = results.equityCurve?.reduce((acc: any, point: any) => {
    const month = new Date(point.date).toLocaleString('default', { month: 'short', year: 'numeric' })
    if (!acc[month]) {
      acc[month] = { month, value: point.value }
    }
    return acc
  }, {}) || {}

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {metrics.map((metric) => (
          <MetricCard key={metric.title} {...metric} />
        ))}
      </div>

      {/* Equity Curve */}
      <Card>
        <CardHeader>
          <CardTitle>Equity Curve</CardTitle>
        </CardHeader>
        <CardContent>
          <TradingLineChart
            data={equityCurveData}
            lines={[
              { dataKey: 'value', name: 'Portfolio Value', color: '#0066cc', strokeWidth: 2 },
            ]}
            xAxisKey="date"
            height={300}
            area={true}
          />
        </CardContent>
      </Card>

      {/* Returns Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Trade Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <div className="text-center space-y-2">
                <div className="flex items-center gap-8">
                  <div>
                    <p className="text-3xl font-bold text-success">{winningTrades}</p>
                    <p className="text-sm text-muted-foreground">Winning Trades</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-destructive">{losingTrades}</p>
                    <p className="text-sm text-muted-foreground">Losing Trades</p>
                  </div>
                </div>
                <ProgressBar
                  value={(winningTrades / results.totalTrades) * 100}
                  max={100}
                  variant="success"
                  showLabel
                  label="Win Rate"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Performance Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Gross Profit</span>
                <span className="font-medium text-success">
                  ${formatNumber(results.trades?.filter((t: any) => t.pnl > 0).reduce((sum: number, t: any) => sum + t.pnl, 0) || 0)}
                </span>
              </div>
              <ProgressBar value={75} max={100} variant="success" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Gross Loss</span>
                <span className="font-medium text-destructive">
                  ${formatNumber(Math.abs(results.trades?.filter((t: any) => t.pnl < 0).reduce((sum: number, t: any) => sum + t.pnl, 0) || 0))}
                </span>
              </div>
              <ProgressBar value={25} max={100} variant="danger" />
            </div>
            <div className="pt-2 border-t border-border">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Net Profit</span>
                <span className={`font-semibold ${results.totalReturn >= 0 ? 'text-success' : 'text-destructive'}`}>
                  ${formatNumber(results.totalReturn)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trade List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Trades</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-2 px-4 text-sm font-medium text-muted-foreground">Symbol</th>
                  <th className="text-left py-2 px-4 text-sm font-medium text-muted-foreground">Type</th>
                  <th className="text-right py-2 px-4 text-sm font-medium text-muted-foreground">Entry</th>
                  <th className="text-right py-2 px-4 text-sm font-medium text-muted-foreground">Exit</th>
                  <th className="text-right py-2 px-4 text-sm font-medium text-muted-foreground">P&L</th>
                </tr>
              </thead>
              <tbody>
                {results.trades?.slice(0, 20).map((trade: any, index: number) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4 text-sm">{new Date(trade.entryDate).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm font-medium">{trade.symbol}</td>
                    <td className="py-3 px-4 text-sm">
                      <Badge variant={trade.type === 'long' ? 'success' : 'danger'}>
                        {trade.type.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-right">{formatNumber(trade.entryPrice)}</td>
                    <td className="py-3 px-4 text-sm text-right">{formatNumber(trade.exitPrice)}</td>
                    <td className={`py-3 px-4 text-sm text-right font-medium ${trade.pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {trade.pnl >= 0 ? '+' : ''}{formatNumber(trade.pnl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
